import { useState, useCallback } from 'react';
import { api } from '../../services/api';
import ToastMessages from '../../components/ToastMessages/toastMessages';

export interface IVehicleData {
  model: string;
  year: string;
  licensePlate: string;
  payloadCapacity: number;
  vehicleType: string;
  dimensions: {
    weight: number;
    length: number;
    width: number;
  };
  crlv: {
    documentNumber: string;
    expirationDate: string;
    isDigital: boolean;
    imageUrlFront: File | string | null;
    imageUrlBack: File | string | null;
  };
  images: {
    imageUrlBack: File | string | null;
    imageUrlFront: File | string | null;
    imageUrlLeft: File | string | null;
    imageUrlRight: File | string | null;
  };
}

const getPersistedToken = (): string => {
  try {
    const persistFretapp = localStorage.getItem('persist:fretapp');
    if (persistFretapp) {
      const parsedRoot = JSON.parse(persistFretapp);
      const distributorString = parsedRoot.distributor;
      if (distributorString) {
        const distributor = JSON.parse(distributorString);
        return distributor?.token || '';
      }
    }
  } catch (error) {
    console.error('[getPersistedToken] Erro ao recuperar token:', error);
  }
  return '';
};

const useBulkCreatePrivateVehicles = () => {
  const [loading, setLoading] = useState(false);

  const execute = useCallback(
    async (vehiclesData: IVehicleData[], onSuccess: () => void) => {
      setLoading(true);
      try {
        const token = getPersistedToken();
        const authHeaders = {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        };

        const createdVehicleIds: string[] = [];

        for (const vehicle of vehiclesData) {
          const vehiclePayload = {
            model: vehicle.model,
            year: vehicle.year,
            licensePlate: vehicle.licensePlate,
            payloadCapacity: vehicle.payloadCapacity,
            vehicleType: vehicle.vehicleType,
            dimensions: vehicle.dimensions,
            images: {
              imageUrlBack: null,
              imageUrlFront: null,
              imageUrlLeft: null,
              imageUrlRight: null,
            },
          };

          const createVehicleResponse = await api.post(
            `/v1/api/vehicles/`,
            vehiclePayload,
            authHeaders,
          );

          const vehicleId = createVehicleResponse.data;
          if (!vehicleId) {
            throw new Error(
              'Falha ao obter o ID do veículo criado para: ' +
              vehicle.licensePlate,
            );
          }
          createdVehicleIds.push(vehicleId);

          const uploadVehicleImage = async (
            file: File | string | null,
            endpointSuffix: string,
            fieldName: string,
          ) => {
            if (file instanceof File) {
              const formData = new FormData();
              formData.append(fieldName, file);
              const uploadUrl = `/v1/api/vehicles/${vehicleId}/${endpointSuffix}`;

              await api.post(uploadUrl, formData, {
                ...authHeaders,
                headers: {
                  ...authHeaders.headers,
                  'Content-Type': 'multipart/form-data',
                },
              });
            }
          };

          await uploadVehicleImage(
            vehicle.images.imageUrlFront,
            'upload-image-url-front',
            'frontVehicle',
          );
          await uploadVehicleImage(
            vehicle.images.imageUrlBack,
            'upload-image-url-back',
            'backVehicle',
          );
          await uploadVehicleImage(
            vehicle.images.imageUrlLeft,
            'upload-image-url-left',
            'leftVehicle',
          );
          await uploadVehicleImage(
            vehicle.images.imageUrlRight,
            'upload-image-url-right',
            'rightVehicle',
          );

          const crlvDataPayload = {
            documentNumber: vehicle.crlv.documentNumber,
            expirationDate: vehicle.crlv.expirationDate,
            isDigital: vehicle.crlv.isDigital,
            plateNumber: vehicle.licensePlate,
          };

          const createCrlvResponse = await api.post(
            `/v1/api/vehicles/${vehicleId}/crlv-data`,
            crlvDataPayload,
            authHeaders,
          );

          const crlvId = createCrlvResponse.data;
          console.log('CRLV ID obtido:', crlvId);
          if (!crlvId) {
            throw new Error(
              `Falha ao obter o ID do CRLV para o veículo: ${vehicle.licensePlate}`,
            );
          }

          const uploadCrlvImage = async (
            file: File | string | null,
            endpointSuffix: string,
            fieldName: string,
          ) => {
            if (file instanceof File) {
              const formData = new FormData();
              formData.append(fieldName, file);
              const crlvUploadUrl = `/v1/api/vehicles/${vehicleId}/crlv/${crlvId}/${endpointSuffix}`;

              await api.put(crlvUploadUrl, formData, {
                ...authHeaders,
                headers: {
                  ...authHeaders.headers,
                  'Content-Type': 'multipart/form-data',
                },
              });
            }
          };

          await uploadCrlvImage(
            vehicle.crlv.imageUrlFront,
            'image-front',
            'crlvFrontImage',
          );
          await uploadCrlvImage(
            vehicle.crlv.imageUrlBack,
            'image-back',
            'crlvBackImage',
          );
        }

        ToastMessages.vehicle.success.createMany(createdVehicleIds.length);
        onSuccess();
      } catch (error: any) {
        console.error('Erro na criação de veículos:', error);
        const msg = error?.response?.data?.message;

        if (msg) {
          ToastMessages.vehicle.error.createMany(`Erro: ${msg}`);
        } else if (error.message?.includes('Falha ao obter o ID')) {
          ToastMessages.vehicle.error.createMany(error.message);
        } else {
          ToastMessages.vehicle.error.createMany(
            'Falha ao criar veículos. Tente novamente.',
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  return { execute, loading };
};

export default useBulkCreatePrivateVehicles;